import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { TRANSLATIONS, LANGUAGES } from "@/i18n/translations";
import api from "@/lib/api";

const I18nContext = createContext(null);

function get(obj, path) {
  return path.split(".").reduce((o, k) => (o && o[k] != null ? o[k] : undefined), obj);
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem("maaprint_locale") || "en";
  });

  useEffect(() => {
    localStorage.setItem("maaprint_locale", locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback(async (next) => {
    setLocaleState(next);
    // Persist to server if signed in — silently ignore failures
    try {
      await api.patch("/auth/locale", { locale: next });
    } catch { /* not signed in */ }
  }, []);

  const t = useCallback(
    (path, vars = {}) => {
      const bundle = TRANSLATIONS[locale] || TRANSLATIONS.en;
      let value = get(bundle, path);
      if (value === undefined) value = get(TRANSLATIONS.en, path);
      if (typeof value !== "string") return path;
      return Object.keys(vars).reduce(
        (s, k) => s.replace(new RegExp(`\\{${k}\\}`, "g"), String(vars[k])),
        value
      );
    },
    [locale]
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, languages: LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
